/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { Callout, LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ResourcesById, AccountResource, AccountUsageResource, StepUsage, ReleaseUsage, ReleaseUsageEntry, LibraryVariableSetUsageEntry, ProjectVariableSetUsage, TargetUsageEntry, RunbookStepUsage, ProjectResource, RunbookResource, RunbookSnapshotUsage, TenantResource, ProjectTenantVariableUsage, CommonTenantVariableUsage, LibraryVariableSetResource, } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { sortBy, uniq, keys, reduce, groupBy, compact, flatten, flatMap, uniqBy } from "lodash";
import * as React from "react";
import { repository } from "~/clientInstance";
import Chip from "~/components/Chips/Chip";
import NavigationChip from "~/components/Chips/NavigationChip";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import DataBaseComponent from "~/components/DataBaseComponent";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { ExpandableFormSection, Summary, FormSectionHeading } from "~/components/form";
import { CardFill } from "~/components/form/Sections/ExpandableFormSection";
import { timeOperationOptions } from "~/utils/OperationTimer/timeOperation";
import toResourceLookup from "~/utils/toResourceLookup";
import type { VersionControlledProjectUsage } from "../VersionControlledProjectUsages/VersionControlledProjectUsages";
import { getVersionControlledProjectUsages, VersionControlledProjectUsages } from "../VersionControlledProjectUsages/VersionControlledProjectUsages";
enum UsageType {
    Target = "Target",
    LibraryVariableSet = "LibraryVariableSet"
}
type ProjectAccountUsage = Pick<AccountUsageResource, "DeploymentProcesses" | "Releases" | "ProjectVariableSets" | "RunbookProcesses" | "RunbookSnapshots">;
type RunbookAccountUsage = Pick<AccountUsageResource, "RunbookProcesses" | "RunbookSnapshots">;
type TenantAccountUsage = Pick<AccountUsageResource, "ProjectTenantVariables" | "CommonTenantVariables">;
type UsageByProjectIdLookup = {
    [key: string]: ProjectAccountUsage;
};
type UsageByRunbookIdLookup = {
    [key: string]: RunbookAccountUsage;
};
type UsageByTenantIdLookup = {
    [key: string]: TenantAccountUsage;
};
type ProjectLookup = ResourcesById<ProjectResource>;
type RunbookLookup = ResourcesById<RunbookResource>;
type TenantLookup = ResourcesById<TenantResource>;
type VariableSetLookup = ResourcesById<LibraryVariableSetResource>;
function lookupUsage<T>(lookup: () => T[], fallback: () => T[]) {
    const result = lookup();
    return result ? result : fallback();
}
function getProjectUsageGroupings(usage: AccountUsageResource): UsageByProjectIdLookup {
    const deploymentProcesses = groupBy(usage.DeploymentProcesses, (x) => x.ProjectId);
    const projectVariables = groupBy(usage.ProjectVariableSets.filter((x) => x.IsCurrentlyBeingUsedInProject === true), (x) => x.ProjectId);
    const releaseUsages = getCombinedReleaseUsages(usage.Releases, usage.ProjectVariableSets.filter((x) => x.IsCurrentlyBeingUsedInProject === false));
    const releases = groupBy(releaseUsages, (x) => x.ProjectId);
    const runbookProcesses = groupBy(usage.RunbookProcesses, (x) => x.ProjectId);
    const runbookSnapshots = groupBy(usage.RunbookSnapshots, (x) => x.ProjectId);
    const initial: {
        [key: string]: ProjectAccountUsage;
    } = {};
    const projectIds = uniq([...keys(deploymentProcesses), ...keys(projectVariables), ...keys(releases), ...keys(runbookProcesses), ...keys(runbookSnapshots)]);
    return reduce(projectIds, (prev, id) => {
        return {
            ...prev,
            [id]: {
                DeploymentProcesses: lookupUsage(() => deploymentProcesses[id], () => [] as StepUsage[]),
                ProjectVariableSets: lookupUsage(() => projectVariables[id], () => [] as ProjectVariableSetUsage[]),
                Releases: lookupUsage(() => releases[id], () => [] as ReleaseUsage[]),
                RunbookProcesses: lookupUsage(() => runbookProcesses[id], () => [] as RunbookStepUsage[]),
                RunbookSnapshots: lookupUsage(() => runbookSnapshots[id], () => [] as RunbookSnapshotUsage[]),
            },
        };
    }, initial);
}
function getCombinedReleaseUsages(releases: ReleaseUsage[], projectVariableSetUsagesViaReleaseSnapshots: ProjectVariableSetUsage[]): ReleaseUsage[] {
    const releaseProjectIds = uniq([...projectVariableSetUsagesViaReleaseSnapshots.map((x) => x.ProjectId), ...releases.map((x) => x.ProjectId)]);
    const releaseUsages = releaseProjectIds.reduce<ReleaseUsage[]>((acc, projectId) => {
        const p1 = projectVariableSetUsagesViaReleaseSnapshots.find((a) => a.ProjectId === projectId);
        const p2 = releases.find((a) => a.ProjectId === projectId);
        const releasesInProjects = uniqBy([...(p1?.Releases ?? []), ...(p2?.Releases ?? [])], (p) => p.ReleaseId);
        const projectName = p1?.ProjectName ?? p2?.ProjectName ?? "";
        acc.push({ ProjectName: projectName, ProjectId: projectId, Releases: releasesInProjects });
        return acc;
    }, []);
    return releaseUsages;
}
function getRunbookUsageGroupings(usage: AccountUsageResource): UsageByRunbookIdLookup {
    const runbookSnapshots = groupBy(usage.RunbookSnapshots, (x) => x.RunbookId);
    const runbookProcesses = groupBy(usage.RunbookProcesses, (x) => x.RunbookId);
    const runbookIds = uniq([...keys(runbookSnapshots), ...keys(runbookProcesses)]);
    const initial: {
        [key: string]: RunbookAccountUsage;
    } = {};
    return reduce(runbookIds, (prev, id) => {
        return {
            ...prev,
            [id]: {
                RunbookProcesses: lookupUsage(() => runbookProcesses[id], () => [] as RunbookStepUsage[]),
                RunbookSnapshots: lookupUsage(() => runbookSnapshots[id], () => [] as RunbookSnapshotUsage[]),
            },
        };
    }, initial);
}
function getTenantUsageGroupings(usage: AccountUsageResource): UsageByTenantIdLookup {
    const projectTenantVariables = groupBy(usage.ProjectTenantVariables, (x) => x.TenantId);
    const commonTenantVariables = groupBy(usage.CommonTenantVariables, (x) => x.TenantId);
    const tenantIds = uniq([...keys(projectTenantVariables), ...keys(commonTenantVariables)]);
    const initial: {
        [key: string]: TenantAccountUsage;
    } = {};
    return reduce(tenantIds, (prev, id) => {
        return {
            ...prev,
            [id]: {
                ProjectTenantVariables: lookupUsage(() => projectTenantVariables[id], () => [] as ProjectTenantVariableUsage[]),
                CommonTenantVariables: lookupUsage(() => commonTenantVariables[id], () => [] as CommonTenantVariableUsage[]),
            },
        };
    }, initial);
}
interface AccountUsageState extends DataBaseComponentState {
    projectUsageLookup: UsageByProjectIdLookup;
    runbookUsageLookup: UsageByRunbookIdLookup;
    projects: ProjectLookup;
    runbooks: RunbookLookup;
    runbooksByProjectId: {
        [key: string]: {
            Id: string;
            Name: string;
            ProjectId: string;
            SpaceId: string;
            RunbookProcessId: string;
        }[];
    };
    tenantUsageLookup: UsageByTenantIdLookup;
    tenants: TenantLookup;
    tenantProjects: ProjectLookup;
    tenantVariableSets: VariableSetLookup;
    versionControlledProjects: VersionControlledProjectUsage[];
}
interface GroupedUsageSectionProps {
    title: string;
    description?: React.ReactNode;
    children?: React.ReactNode;
}
const GroupedUsageSection: React.FC<GroupedUsageSectionProps> = ({ title: name, description, children }) => {
    return (<div>
            <div className={groupUsageSectionStyles.header}>
                <div className={groupUsageSectionStyles.nameColumn}>
                    <h2 className={groupUsageSectionStyles.name}>{name}</h2>
                </div>
                <div className={groupUsageSectionStyles.descriptionColumn}>{description}</div>
            </div>

            {children}
        </div>);
};
GroupedUsageSection.displayName = "GroupedUsageSection"
const groupUsageSectionStyles = {
    header: css({
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
    }),
    nameColumn: css({
        flex: 1,
    }),
    descriptionColumn: css({
        flex: 5,
    }),
    name: css({
        font: text.heading.small,
        margin: `${space[8]} 0`,
    }),
};
interface UsageGroupProps {
    name: React.ReactNode;
    children?: React.ReactNode;
}
const UsageGroup: React.FC<UsageGroupProps> = ({ name, children }) => {
    return (<div>
            <h3 className={usageGroupStyles.heading}>{name}</h3>
            {children}
        </div>);
};
UsageGroup.displayName = "UsageGroup"
const usageGroupStyles = {
    heading: css({
        background: themeTokens.color.background.tertiary,
        padding: space[8],
        font: text.heading.small,
        borderBottom: `solid 1px ${themeTokens.color.border.primary}`,
        borderTop: `solid 1px ${themeTokens.color.border.primary}`,
        margin: `${space[8]} 0`,
    }),
};
interface UsageRowProps {
    name: string;
    children: React.ReactNode;
}
const UsageRow: React.FC<UsageRowProps> = ({ name, children }) => {
    return (<div className={usageRowStyles.container}>
            <div className={usageRowStyles.nameColumn}>{name}</div>
            <div className={usageRowStyles.descriptionColumn}>{children}</div>
        </div>);
};
UsageRow.displayName = "UsageRow"
const usageRowStyles = {
    container: css({
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        padding: `${space[16]} ${space[8]}`,
        borderBottom: `solid 1px ${themeTokens.color.border.primary}`,
    }),
    nameColumn: css({
        flex: 1,
    }),
    descriptionColumn: css({
        flex: 5,
    }),
};
interface ReleaseUsageRowProps {
    spaceId: string;
    usages: ReleaseUsage[];
}
const ReleaseUsageRow: React.FC<ReleaseUsageRowProps> = ({ spaceId, usages }) => {
    return (<React.Fragment>
            {usages && usages.length > 0 && (<UsageRow name={"Releases"}>
                    {usages.map((usageEntry: ReleaseUsage) => usageEntry.Releases.map((release: ReleaseUsageEntry, i) => (<NavigationChip to={links.releasePage.generateUrl({ spaceId, projectSlug: usageEntry.ProjectId, releaseVersion: release.ReleaseVersion })} key={`${release.ReleaseId}`} accessibleName={`Release version ${release.ReleaseVersion} for project ${usageEntry.ProjectName}`}>
                                {release.ReleaseVersion}
                            </NavigationChip>)))}
                </UsageRow>)}
        </React.Fragment>);
};
ReleaseUsageRow.displayName = "ReleaseUsageRow"
interface RunbookSnapshotUsageRowProps {
    spaceId: string;
    usages: RunbookSnapshotUsage[];
}
const RunbookSnapshotUsageRow: React.FC<RunbookSnapshotUsageRowProps> = ({ spaceId, usages }) => {
    return (<React.Fragment>
            {usages && usages.length > 0 && (<UsageRow name={"Snapshots"}>
                    {usages.map((usageEntry) => usageEntry.Snapshots.map((snapshot) => (<NavigationChip to={links.runbookSnapshotPage.generateUrl({ spaceId, projectSlug: usageEntry.ProjectId, runbookId: usageEntry.RunbookId, runbookSnapshotId: snapshot.SnapshotId })} key={snapshot.SnapshotId} accessibleName={`Runbook snapshot ${snapshot.SnapshotName} for runbook ${usageEntry.RunbookName} in project ${usageEntry.ProjectName}`}>
                                {snapshot.SnapshotName}
                            </NavigationChip>)))}
                </UsageRow>)}
        </React.Fragment>);
};
RunbookSnapshotUsageRow.displayName = "RunbookSnapshotUsageRow"
interface DeploymentProcessStepsUsageRowProps {
    spaceId: string;
    usages: StepUsage[];
}
const DeploymentProcessStepsUsageRow: React.FC<DeploymentProcessStepsUsageRowProps> = ({ spaceId, usages }) => {
    return (<React.Fragment>
            {usages && usages.length > 0 && (<UsageRow name={"Steps"}>
                    {usages.map((usageEntry) => usageEntry.Steps.map((step) => (<NavigationChip to={links.deploymentProcessEditorPage.generateUrl({ spaceId, projectSlug: usageEntry.ProjectSlug }, { actionId: step.StepId })} key={`${step.StepId}`} accessibleName={`Deployment process step ${step.StepName} for project ${usageEntry.ProjectName}`}>
                                {step.StepName}
                            </NavigationChip>)))}
                </UsageRow>)}
        </React.Fragment>);
};
DeploymentProcessStepsUsageRow.displayName = "DeploymentProcessStepsUsageRow"
interface RunbookProcessStepsUsageRowProps {
    spaceId: string;
    usages: RunbookStepUsage[];
}
const RunbookProcessStepsUsageRow: React.FC<RunbookProcessStepsUsageRowProps> = ({ spaceId, usages }) => {
    return (<React.Fragment>
            {usages && usages.length > 0 && (<UsageRow name={"Steps"}>
                    {usages.map((usageEntry: RunbookStepUsage) => usageEntry.Steps.map((step) => (<NavigationChip to={links.runbookProcessEditorPage.generateUrl({ spaceId, projectSlug: usageEntry.ProjectSlug, processId: usageEntry.ProcessId, runbookId: usageEntry.RunbookId }, { actionId: step.StepId })} key={`${step.StepId}`} accessibleName={`Runbook step ${step.StepName} for runbook ${usageEntry.RunbookName} in project ${usageEntry.ProjectName}`}>
                                {step.StepName}
                            </NavigationChip>)))}
                </UsageRow>)}
        </React.Fragment>);
};
RunbookProcessStepsUsageRow.displayName = "RunbookProcessStepsUsageRow"
const getCountSummary = (items: ArrayLike<{}>, singular: string, plural: string) => {
    return (items &&
        items.length > 0 && (<Chip>
                {items.length} {items.length === 1 ? singular : plural}
            </Chip>));
};
const getProjectSummary = (usages: ProjectAccountUsage & RunbookAccountUsage = { DeploymentProcesses: [], ProjectVariableSets: [], Releases: [], RunbookProcesses: [], RunbookSnapshots: [] }) => {
    const usageSummary = flatten(compact([
        getCountSummary(flatMap(usages.DeploymentProcesses || [], (x) => x.Steps), "deployment process step", "deployment process steps"),
        getCountSummary(flatMap(usages.Releases || [], (x) => x.Releases), "release", "releases"),
        getCountSummary(flatMap(usages.RunbookProcesses || [], (x) => x.Steps), "runbook process step", "runbook process steps"),
        getCountSummary(flatMap(usages.RunbookSnapshots || [], (x) => x.Snapshots), "runbook snapshot", "runbook snapshots"),
        usages.ProjectVariableSets && usages.ProjectVariableSets.length > 0 && `is used in variables`,
    ]).map((value, index) => (index === 0 ? [value] : [" and ", value])));
    return <React.Fragment>This account is used in {usageSummary}</React.Fragment>;
};
export class AccountUsageInternal extends DataBaseComponent<AccountUsageProps, AccountUsageState> {
    constructor(props: AccountUsageProps) {
        super(props);
        this.state = {
            projectUsageLookup: {},
            projects: {},
            runbooks: {},
            runbooksByProjectId: {},
            runbookUsageLookup: {},
            tenantUsageLookup: {},
            tenants: {},
            tenantProjects: {},
            tenantVariableSets: {},
            versionControlledProjects: [],
        };
    }
    async componentDidMount() {
        return this.doBusyTask(async () => {
            const projectUsageLookup = getProjectUsageGroupings(this.props.accountUsages);
            const runbookUsageLookup = getRunbookUsageGroupings(this.props.accountUsages);
            const runbooks = uniqBy([
                ...this.props.accountUsages.RunbookProcesses.map((x) => ({
                    Id: x.RunbookId,
                    Name: x.RunbookName,
                    ProjectId: x.ProjectId,
                    SpaceId: this.props.account.SpaceId,
                    RunbookProcessId: x.ProcessId,
                })),
                ...this.props.accountUsages.RunbookSnapshots.map((x) => ({
                    Id: x.RunbookId,
                    Name: x.RunbookName,
                    ProjectId: x.ProjectId,
                    SpaceId: this.props.account.SpaceId,
                    RunbookProcessId: x.Snapshots[0].SnapshotId,
                })),
            ], (x) => `${x.Id}-${x.ProjectId}` // Use a combination of properties to ensure uniqueness
            );
            const versionControlledProjects = await getVersionControlledProjectUsages(repository);
            const projectsWithDirectUsage = await repository.Projects.all({
                ids: uniq([...keys(projectUsageLookup), ...this.props.accountUsages.RunbookProcesses.map((x) => x.ProjectId), ...this.props.accountUsages.RunbookSnapshots.map((x) => x.ProjectId)]),
            });
            const runbookByProjectId = groupBy(runbooks, (x) => x.ProjectId);
            const tenantUsageLookup = getTenantUsageGroupings(this.props.accountUsages);
            const tenants = await repository.Tenants.all({ ids: keys(tenantUsageLookup) });
            const tenantProjects = await repository.Projects.all({ ids: uniq([...this.props.accountUsages.ProjectTenantVariables.flatMap((p) => p.Projects.flatMap((pv) => pv.ProjectId))]) });
            const tenantVariableSets = await repository.LibraryVariableSets.all({ ids: uniq([...this.props.accountUsages.CommonTenantVariables.flatMap((c) => c.LibraryVariableSets.flatMap((cv) => cv.LibraryVariableSetId))]) });
            this.setState({
                projects: toResourceLookup(projectsWithDirectUsage),
                projectUsageLookup,
                runbooksByProjectId: runbookByProjectId,
                runbookUsageLookup,
                tenantUsageLookup,
                tenants: toResourceLookup(tenants),
                tenantProjects: toResourceLookup(tenantProjects),
                tenantVariableSets: toResourceLookup(tenantVariableSets),
                versionControlledProjects,
            });
        }, { timeOperationOptions: timeOperationOptions.for("AccountUsage") });
    }
    render() {
        const accountUsages = this.props.accountUsages;
        return (<div>
                <ExpandableFormSection key="usageInLibraryVariableSets" errorKey="usageInLibraryVariableSets" title="Variable Sets" summary={this.accountUsageSummary(UsageType.LibraryVariableSet, accountUsages)} help={this.accountUsageHelp(UsageType.LibraryVariableSet, accountUsages)}>
                    {this.accountUsageInLibraryVariableSets(accountUsages)}
                </ExpandableFormSection>
                <ExpandableFormSection key="usageInTargets" errorKey="usageInTargets" title="Targets" summary={this.accountUsageSummary(UsageType.Target, accountUsages)} help={this.accountUsageHelp(UsageType.Target, accountUsages)}>
                    {this.accountUsageInTargets(accountUsages)}
                </ExpandableFormSection>

                <FormSectionHeading title={"Projects"}/>
                {keys(this.state.projects).map((x) => {
                return (<ExpandableFormSection fillCardWidth={CardFill.FillRight} key={x} errorKey={x} title={this.state.projects[x].Name} summary={Summary.summary(getProjectSummary({ ...this.state.projectUsageLookup[x] }))} help={"Areas of this project linked to this account."}>
                            {this.state.projectUsageLookup[x] && (<GroupedUsageSection title={"Deployment"}>
                                    <UsageGroup name={<InternalLink to={links.deploymentProcessPage.generateUrl({
                                spaceId: this.props.account.SpaceId,
                                projectSlug: this.state.projects[x].Slug,
                            })}>
                                                Process
                                            </InternalLink>}>
                                        <ReleaseUsageRow spaceId={this.props.account.SpaceId} usages={this.state.projectUsageLookup[x].Releases}/>
                                        <DeploymentProcessStepsUsageRow spaceId={this.props.account.SpaceId} usages={this.state.projectUsageLookup[x].DeploymentProcesses}/>
                                    </UsageGroup>
                                </GroupedUsageSection>)}
                            {this.state.runbooksByProjectId[x] && (<GroupedUsageSection title={"Runbooks"}>
                                    {this.state.runbooksByProjectId[x].map(({ Id: runbookId, Name: name, RunbookProcessId: runbookProcessId, SpaceId: spaceId }) => (<UsageGroup name={<InternalLink to={links.runbookProcessPage.generateUrl({ spaceId, projectSlug: this.state.projects[x].Slug, runbookId, processId: runbookProcessId })}>{name}</InternalLink>} key={runbookId}>
                                            <RunbookSnapshotUsageRow spaceId={spaceId} usages={this.state.runbookUsageLookup[runbookId].RunbookSnapshots}/>
                                            <RunbookProcessStepsUsageRow spaceId={spaceId} usages={this.state.runbookUsageLookup[runbookId].RunbookProcesses}/>
                                        </UsageGroup>))}
                                </GroupedUsageSection>)}
                            {this.state.projectUsageLookup[x] && this.state.projectUsageLookup[x].ProjectVariableSets.length > 0 && (<GroupedUsageSection title={"Variables"} description={<InternalLink to={links.projectVariablesPage.generateUrl({ spaceId: this.state.projects[x].SpaceId, projectSlug: this.state.projects[x].Slug })}>
                                            This account is being used in this projects variables
                                        </InternalLink>}/>)}
                        </ExpandableFormSection>);
            })}
                <ExpandableFormSection key="usageInVersionControlledProjects" errorKey="usageInVersionControlledProjects" title="Version Controlled Projects" summary={this.accountUsageInVersionControlledProjectsSummary(this.state.versionControlledProjects)} help={this.accountUsageInVersionControlledProjectsHelp(this.state.versionControlledProjects)} fillCardWidth={CardFill.FillRight}>
                    <Callout type="information" title="Usages in version controlled projects">
                        <p>Deployment processes, runbooks and variables are stored in Git repositories for version controlled projects. Usages above for projects do not include Git repositories for performance reasons.</p>
                        <p>
                            Please check <code>.ocl</code> files for the account slug <code>{this.props.account.Slug}</code> in the Git repository for the following version controlled projects before deleting this account.
                        </p>
                    </Callout>
                    <VersionControlledProjectUsages versionControlledProjects={this.state.versionControlledProjects}/>
                </ExpandableFormSection>
                <FormSectionHeading title={"Tenants"}/>
                {keys(this.state.tenants).map((x) => {
                return (<ExpandableFormSection fillCardWidth={CardFill.FillRight} key={x} errorKey={x} title={this.state.tenants[x].Name} summary={Summary.summary(<span>This account is being used in tenant variables</span>)} help={"This account is being used in the following tenant variables"}>
                            {this.accountUsageInProjectTenantVariables(this.state.tenantUsageLookup[x].ProjectTenantVariables, this.state.tenants[x])}
                            <br />
                            {this.accountUsageInCommonTenantVariables(this.state.tenantUsageLookup[x].CommonTenantVariables, this.state.tenants[x])}
                        </ExpandableFormSection>);
            })}
            </div>);
    }
    accountUsageInVersionControlledProjectsSummary(versionControlledProjects: VersionControlledProjectUsage[]) {
        return versionControlledProjects.length > 0
            ? versionControlledProjects.length > 1
                ? Summary.summary(<span>
                          This account may be used in <b>{versionControlledProjects.length}</b> version controlled projects
                      </span>)
                : Summary.summary(<span>
                          This account may be used in <b>1</b> version controlled project
                      </span>)
            : Summary.placeholder("This account is not being used in any version controlled projects");
    }
    accountUsageInVersionControlledProjectsHelp(versionControlledProjects: VersionControlledProjectUsage[]) {
        return versionControlledProjects.length === 0
            ? "This account is not being used in any version controlled projects"
            : versionControlledProjects.length === 1
                ? "This account may be used in the following version controlled project"
                : "This account may be used in the following version controlled projects";
    }
    accountUsageSummary(usageType: UsageType, accountUsages: AccountUsageResource) {
        switch (usageType) {
            case UsageType.Target:
                return accountUsages.Targets.length > 0
                    ? accountUsages.Targets.length > 1
                        ? Summary.summary(<span>
                                  This account is being used in <b>{accountUsages.Targets.length}</b> targets
                              </span>)
                        : Summary.summary(<span>
                                  This account is being used in <b>1</b> target
                              </span>)
                    : Summary.placeholder("This account is not being used in any targets");
            case UsageType.LibraryVariableSet:
                return accountUsages.LibraryVariableSets.length > 0
                    ? accountUsages.LibraryVariableSets.length > 1
                        ? Summary.summary(<span>
                                  This account is being used in <b>{accountUsages.LibraryVariableSets.length}</b> variable sets
                              </span>)
                        : Summary.summary(<span>
                                  This account is being used in <b>1</b> variable set
                              </span>)
                    : Summary.placeholder("This account is not being used in any variable sets");
        }
    }
    accountUsageHelp(usageType: UsageType, accountUsages: AccountUsageResource) {
        switch (usageType) {
            case UsageType.Target:
                return accountUsages.Targets.length === 0
                    ? "This account is not being used in any targets"
                    : accountUsages.Targets.length === 1
                        ? "This account is being used in the following target"
                        : "This account is being used in the following targets";
            case UsageType.LibraryVariableSet:
                return accountUsages.LibraryVariableSets.length === 0
                    ? "This account is not being used in any variable sets"
                    : accountUsages.LibraryVariableSets.length === 1
                        ? "This account is being used in the following variable set"
                        : "This account is being used in the following variable sets";
        }
    }
    private accountUsageInLibraryVariableSets(accountUsages: AccountUsageResource) {
        return (<div>
                {accountUsages.LibraryVariableSets.length > 0 && (<LegacyDataTable>
                        <LegacyDataTableHeader>
                            <LegacyDataTableRow>
                                <LegacyDataTableHeaderColumn>Variable Set</LegacyDataTableHeaderColumn>
                            </LegacyDataTableRow>
                        </LegacyDataTableHeader>
                        <LegacyDataTableBody>
                            {sortBy(accountUsages.LibraryVariableSets, [(x) => x.LibraryVariableSetName, "asc"]).map((usageEntry: LibraryVariableSetUsageEntry, idx) => {
                    const rowKey = `AULVS-${usageEntry.LibraryVariableSetId}`;
                    return (<LegacyDataTableRow key={rowKey}>
                                        <LegacyDataTableRowColumn>
                                            <InternalLink to={links.variableSetPage.generateUrl({ spaceId: this.props.account.SpaceId, variableSetId: usageEntry.LibraryVariableSetId })}>{usageEntry.LibraryVariableSetName}</InternalLink>
                                        </LegacyDataTableRowColumn>
                                    </LegacyDataTableRow>);
                })}
                        </LegacyDataTableBody>
                    </LegacyDataTable>)}
            </div>);
    }
    private accountUsageInTargets(accountUsages: AccountUsageResource) {
        return (<div>
                {accountUsages.Targets.length > 0 && (<LegacyDataTable>
                        <LegacyDataTableHeader>
                            <LegacyDataTableRow>
                                <LegacyDataTableHeaderColumn>Target Name</LegacyDataTableHeaderColumn>
                            </LegacyDataTableRow>
                        </LegacyDataTableHeader>
                        <LegacyDataTableBody>
                            {sortBy(accountUsages.Targets, [(x) => x.TargetName, "asc"]).map((usageEntry: TargetUsageEntry, idx) => {
                    const rowKey = `AUT-${usageEntry.TargetId}`;
                    return (<LegacyDataTableRow key={rowKey}>
                                        <LegacyDataTableRowColumn>
                                            <InternalLink to={links.deploymentTargetSettingsPage.generateUrl({ spaceId: this.props.account.SpaceId, machineId: usageEntry.TargetId })}>{usageEntry.TargetName}</InternalLink>
                                        </LegacyDataTableRowColumn>
                                    </LegacyDataTableRow>);
                })}
                        </LegacyDataTableBody>
                    </LegacyDataTable>)}
            </div>);
    }
    private accountUsageInProjectTenantVariables(projectTenantVariablesUsage: ProjectTenantVariableUsage[], tenant: TenantResource) {
        return (<div>
                {projectTenantVariablesUsage && projectTenantVariablesUsage.length > 0 && (<LegacyDataTable>
                        <LegacyDataTableHeader>
                            <LegacyDataTableRow>
                                <LegacyDataTableHeaderColumn>Project Template</LegacyDataTableHeaderColumn>
                            </LegacyDataTableRow>
                        </LegacyDataTableHeader>
                        <LegacyDataTableBody>
                            {projectTenantVariablesUsage.map(({ Projects: projects }) => projects.map(({ ProjectId: projectId }) => {
                    const rowKey = `AUPTV-${projectId}`;
                    return (<LegacyDataTableRow key={rowKey}>
                                            <LegacyDataTableRowColumn>
                                                <InternalLink to={links.tenantVariablesPage.generateUrl({ spaceId: this.props.account.SpaceId, tenantId: tenant.Id }, { activeTab: "projectVariables" })}>
                                                    {this.state.tenantProjects[projectId].Name}
                                                </InternalLink>
                                            </LegacyDataTableRowColumn>
                                        </LegacyDataTableRow>);
                }))}
                        </LegacyDataTableBody>
                    </LegacyDataTable>)}
            </div>);
    }
    private accountUsageInCommonTenantVariables(commonTenantVariablesUsage: CommonTenantVariableUsage[], tenant: TenantResource) {
        return (<div>
                {commonTenantVariablesUsage.length > 0 && (<LegacyDataTable>
                        <LegacyDataTableHeader>
                            <LegacyDataTableRow>
                                <LegacyDataTableHeaderColumn>Variable Set</LegacyDataTableHeaderColumn>
                            </LegacyDataTableRow>
                        </LegacyDataTableHeader>
                        <LegacyDataTableBody>
                            {commonTenantVariablesUsage.map(({ LibraryVariableSets: libraryVariableSets }) => libraryVariableSets.map(({ LibraryVariableSetId: libraryVariableSetId }) => {
                    const rowKey = `AUCTV-${libraryVariableSetId}`;
                    return (<LegacyDataTableRow key={rowKey}>
                                            <LegacyDataTableRowColumn>
                                                <InternalLink to={links.tenantVariablesPage.generateUrl({ spaceId: this.props.account.SpaceId, tenantId: tenant.Id }, { activeTab: "commonVariables" })}>
                                                    {this.state.tenantVariableSets[libraryVariableSetId].Name}
                                                </InternalLink>
                                            </LegacyDataTableRowColumn>
                                        </LegacyDataTableRow>);
                }))}
                        </LegacyDataTableBody>
                    </LegacyDataTable>)}
            </div>);
    }
    static displayName = "AccountUsageInternal";
}
class AccountUsageProps {
    account: AccountResource = undefined!;
    accountUsages: AccountUsageResource = undefined!;
}
export default function AccountUsage(props: AccountUsageProps) {
    return <AccountUsageInternal {...props}/>;
}
export { UsageType };
